import { tokenService, tokenUtils } from './tokenService'
import { AUTH_PARAMS } from './auth-utils'
import { createUserTokenMismatchError, createLoginFailedError, isAuthError, AuthErrorCodes } from './auth-errors'
import { userServiceEndpoints, lenderBackendEndpoints } from './auth-client.http'
import authLogger from './auth-logger'

export class LoginResponse {
  accessToken

  constructor(accessToken) {
    this.accessToken = accessToken
  }
}

export class SessionResponse {
  accessToken

  constructor(accessToken) {
    this.accessToken = accessToken
  }
}

export class RedirectCallbackResponse {
  encompassLoanId
  reggoraOrigin
  redirectUrl
  accessToken

  constructor(accessToken, encompassLoanId, reggoraOrigin, redirectUrl) {
    this.accessToken = accessToken
    this.encompassLoanId = encompassLoanId
    this.reggoraOrigin = reggoraOrigin
    this.redirectUrl = redirectUrl
  }
}

/**
 * Auth client is meant to be used soley by the `AuthProvider`, not directly via other parts of the app.
 * It handles the core authentication operations such as login, logout, session checking, and user retrieval.
 * It is the only responsible party for setting and removing tokens from the `tokenService`.
 */
export class AuthClient {
  constructor(identityLogins = false, useFrame = false) {
    this.useIdentityLogins = identityLogins
    this.useFrame = useFrame
    this.currentUser = null
  }

  /**
   * checkSession verifies if the current session is valid, typically used on app load.
   * If sessions are enforced, a session must exist.
   * If sessions are not enforced, a bearer token will be validated.
   * @returns {Promise<undefined>}
   */
  checkSession = async () => {
    authLogger.debug('checking session:', { useIdentityLogins: this.useIdentityLogins })
    try {
      // In the short / medium term, we will check status with both the existing bearer token and the session cookie.
      // This is to support both flows while we transition fully to sessions.
      const mountedBearerToken = tokenService.getToken()
      if (this.useIdentityLogins) {
        const statusOptions = { useProxy: this.useFrame }
        const { access_token: updatedToken } = await userServiceEndpoints.STATUS(mountedBearerToken, statusOptions)
        tokenService.setToken(updatedToken)
      } else {
        const mountedBearerToken = tokenService.getToken()
        if (!tokenService.isTokenValid(mountedBearerToken)) {
          throw createLoginFailedError()
        }
        tokenService.setToken(mountedBearerToken)
      }
    } catch (error) {
      // We only need to be concerned about clearing the bearer token out since the session lifecycle is managed by the backend.
      tokenService.removeToken()

      if (isAuthError(error)) {
        // If its already an AuthError, just rethrow it
        if ([AuthErrorCodes.LOGIN_FAILED].includes(error.code)) {
          // Login failed is expected so don't log an error
          throw error
        }

        authLogger.error('check session failed:', { useIdentityLogins: this.useIdentityLogins, authErrorCode: error.code }, error)
        throw error
      }

      authLogger.error('check session failed:', { useIdentityLogins: this.useIdentityLogins }, error)
      throw createLoginFailedError()
    }
  }

  /**
   * getToken gets a new bearer token.
   * Requires a session to be active.
   * @returns {Promise<SessionResponse>}
   */
  getToken = async () => {
    authLogger.debug('get new token', { useIdentityLogins: this.useIdentityLogins })

    const tokenResponse = await userServiceEndpoints.TOKEN({ useProxy: this.useFrame })
    return new SessionResponse(tokenResponse.access_token)
  }

  getUser = async () => {
    const token = await tokenService.getTokenAsync()
    if (!token) {
      this.currentUser = null
      return this.currentUser
    }

    if (this.currentUser) {
      const tokenUser = tokenUtils.getUserIDFromToken(token)
      if (!tokenUser || tokenUser !== this.currentUser.id) {
        authLogger.warn('token user mismatch, clearing cached user', { useIdentityLogins: this.useIdentityLogins })
        this.currentUser = null
        throw createUserTokenMismatchError()
      }

      return this.currentUser
    }

    try {
      this.currentUser = await lenderBackendEndpoints.CURRENT_USER(token)
    } catch (error) {
      authLogger.error('failed to fetch current user:', { useIdentityLogins: this.useIdentityLogins }, error)
      this.currentUser = null
    }

    return this.currentUser
  }

  clearUser = async () => {
    this.currentUser = null
  }

  /**
   * Log in a user, verifying username and password combination for lender account
   * @param {username} username - username for password login
   * @param {password} password - password for password login
   * @param {lender_id} lender_id - lender account ID for login
   * @returns {Promise<LoginResponse>} - login response
   */
  login = async ({ username, password, lender_id }) => {
    authLogger.debug('login from credentials:', { useIdentityLogins: this.useIdentityLogins })
    if (this.useIdentityLogins) {
      const loginOptions = { useProxy: this.useFrame }
      const loginResult = await userServiceEndpoints.LOGIN(username, password, lender_id, loginOptions)
      tokenService.setToken(loginResult.access_token)
      return new LoginResponse(loginResult.access_token)
    } else {
      const loginResult = await lenderBackendEndpoints.LOGIN(username, password, lender_id)
      tokenService.setToken(loginResult.token)
      return new LoginResponse(loginResult.token)
    }
  }

  /**
   * Logs a user in when loading the app with login redirect parameters
   * @returns {Promise<RedirectCallbackResponse>}
   */
  loginRedirectCallback = async () => {
    authLogger.debug('login from redirect:', { useIdentityLogins: this.useIdentityLogins })
    const currentSearchParams = new URLSearchParams(window.location.search)

    const loginCode = currentSearchParams.get(AUTH_PARAMS.LOGIN_CODE)
    const legacyAuthToken = currentSearchParams.get(AUTH_PARAMS.AUTH_TOKEN)

    if (!loginCode && !legacyAuthToken) {
      throw createLoginFailedError()
    }

    let newAuthToken

    const proxyOptions = { useProxy: this.useFrame }

    if (loginCode) {
      authLogger.debug('login with code', { useIdentityLogins: this.useIdentityLogins })
      const exchangeResponse = await userServiceEndpoints.LOGIN_CODE(loginCode, proxyOptions)
      newAuthToken = exchangeResponse.access_token
    }

    if (legacyAuthToken) {
      authLogger.debug('login with token', { useIdentityLogins: this.useIdentityLogins })
      try {
        const exchangeResponse = await userServiceEndpoints.LOGIN_TOKEN(legacyAuthToken, proxyOptions)
        newAuthToken = exchangeResponse.access_token
      } catch (error) {
        throw createLoginFailedError()
      }
    }

    const reggoraOrigin = currentSearchParams.get(AUTH_PARAMS.ORIGIN)
    const encompassLoanId = currentSearchParams.get(AUTH_PARAMS.ENCOMPASS_LOAN_ID)
    const redirectUrl = currentSearchParams.get(AUTH_PARAMS.REDIRECT_URL)
    const accessToken = newAuthToken ?? legacyAuthToken
    tokenService.setToken(accessToken)
    const response = new RedirectCallbackResponse(accessToken, encompassLoanId, reggoraOrigin, redirectUrl)
    return response
  }

  /**
   * @returns {Promise<void>}
   */
  logout = async () => {
    authLogger.debug('logging out:', { useIdentityLogins: this.useIdentityLogins })
    const currentBearerToken = tokenService.getToken()
    try {
      await lenderBackendEndpoints.LOGOUT(currentBearerToken)
    } catch (error) {
      authLogger.error('logout failed:', { useIdentityLogins: this.useIdentityLogins }, error)
    }

    try {
      await userServiceEndpoints.LOGOUT(currentBearerToken, { useProxy: this.useFrame })
    } catch (error) {
      authLogger.error('logout failed:', { useIdentityLogins: this.useIdentityLogins }, error)
    } finally {
      // Clear cached user data on logout
      tokenService.removeToken()
      this.currentUser = null
      this.encompassLoanId = null
      this.reggoraOrigin = null
      this.redirectUrl = null
    }
  }
}
